import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress
} from '@mui/material';
import api from '../../utils/api';

const AssignUserDialog = ({ open, onClose, onAssign, branchId }) => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [isBranchAdmin, setIsBranchAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchAvailableUsers = useCallback(async () => {
    try {
      // Get all business users and filter out those already in the branch
      const [allUsersRes, branchUsersRes] = await Promise.all([
        api.get('/admin/users').catch(err => {
          throw new Error(`Failed to fetch all users: ${err.response?.data?.message || err.message}`);
        }),
        api.get(`/user-branches/branch/${branchId}/users?include_inactive=true`).catch(err => {
          throw new Error(`Failed to fetch branch users: ${err.response?.data?.message || err.message}`);
        })
      ]);

      // Only exclude users who are ACTIVELY assigned to this branch
      const activeBranchUserIds = new Set(
        branchUsersRes.data.users
          .filter(u => u.status === 'active')
          .map(u => u.id)
      );
      const availableUsers = allUsersRes.data.users.filter(u => !activeBranchUserIds.has(u.id));

      setUsers(availableUsers);
      setLoading(false);
    } catch (err) {
      setError(err.message || 'Failed to load available users');
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    if (open) {
      fetchAvailableUsers();
    }
  }, [open, branchId, fetchAvailableUsers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUser) {
      setError('Please select a user');
      return;
    }

    try {
      await onAssign(selectedUser, isBranchAdmin);
      handleClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to assign user');
    }
  };

  const handleClose = () => {
    setSelectedUser('');
    setIsBranchAdmin(false);
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Assign User to Branch</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <FormControl fullWidth>
                  <InputLabel>Select User</InputLabel>
                  <Select
                    value={selectedUser}
                    onChange={(e) => setSelectedUser(e.target.value)}
                    label="Select User"
                    sx={{ minHeight: 56 }}
                  >
                    {users.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.first_name} {user.last_name} ({user.email})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControlLabel
                  control={
                    <Switch
                      checked={isBranchAdmin}
                      onChange={(e) => setIsBranchAdmin(e.target.checked)}
                    />
                  }
                  label="Assign as Branch Admin"
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !selectedUser}
          >
            Assign User
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AssignUserDialog; 